import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import client, { apiErr } from "../lib/api";
import { Button, Modal } from "../components/ui";
import BrandFooter, { useBranding } from "../components/BrandFooter";
import { BRAND, LANGS, tp } from "../i18n";
import { TreePine, HeartHandshake, Coins, Boxes, Layers, Play, Copy, Monitor, FlaskConical, Link2, Languages } from "lucide-react";

const ICONS = { family_tree: TreePine, benefits: HeartHandshake, currency: Coins, holdings: Boxes };

function MultiScreenModal({ open, onClose, lang }) {
  return (
    <Modal open={open} onClose={onClose} title="Split-screen & multi-screen demos" testid="demo-multiscreen-modal">
      <div className="space-y-3 text-sm">
        <p className="opacity-80">Every demo, support, live and Admin view can run side by side. Because a browser shares its
          sign-in across tabs, run <b>different accounts in separate browser windows or profiles</b>:</p>
        <ul className="list-disc space-y-1.5 ps-5 opacity-80">
          <li><b>Same account, two panes:</b> open a second window with the button below and snap them side by side
            (<span className="rounded bg-black/10 px-1 dark:bg-white/10">Win + ◄/►</span> on Windows,
            <span className="rounded bg-black/10 px-1 dark:bg-white/10"> Ctrl + ⌘ + F</span> then tile on macOS).</li>
          <li><b>Different demos at once:</b> open each in a separate <b>Incognito/Private window</b> or a distinct
            <b> browser profile</b> so their sessions stay independent.</li>
          <li><b>{tp("Demo + Admin + Support together:", lang)}</b> sign in to each role in its own window/profile and tile them across monitors.</li>
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => window.open(window.location.href, "_blank", "width=1100,height=800")} data-testid="demo-open-window">
            <Monitor size={15} className="me-1.5" /> {tp("Open another demo window", lang)}
          </Button>
          <Button onClick={onClose} data-testid="demo-multiscreen-close">{tp("Got it", lang)}</Button>
        </div>
      </div>
    </Modal>
  );
}

function AccountRow({ acct, onLaunch, busy, lang }) {
  const [copied, setCopied] = useState("");
  const copy = (txt, what) => { navigator.clipboard?.writeText(txt); setCopied(what); setTimeout(() => setCopied(""), 1200); };
  const shareLink = `${window.location.origin}/demo-login?launch=${acct.username}&lang=${lang}`;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-black/10 bg-white/60 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5"
      data-testid={`demo-acct-${acct.username}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{acct.name}</div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs opacity-70">
          <button className="hover:underline" onClick={() => copy(acct.username, "user")} title={tp("Copy username", lang)}>
            user: <b>{acct.username}</b> <Copy size={10} className="inline" />
          </button>
          <button className="hover:underline" onClick={() => copy(acct.password, "pass")} title={tp("Copy password", lang)}>
            pass: <b>{acct.password}</b> <Copy size={10} className="inline" />
          </button>
          <button className="hover:underline text-[var(--primary)]" onClick={() => copy(shareLink, "link")}
            title={tp("Copy a shareable link that opens this demo directly", lang)} data-testid={`demo-share-${acct.username}`}>
            <Link2 size={11} className="inline" /> share link
          </button>
          {copied && <span className="text-[var(--primary)]">copied {copied}!</span>}
        </div>
      </div>
      <Button onClick={() => onLaunch(acct)} disabled={busy} data-testid={`demo-launch-${acct.username}`}>
        <Play size={14} className="me-1.5" /> {tp("Launch", lang)}
      </Button>
    </div>
  );
}

export default function DemoLogin() {
  const { login, lang, setLang } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ms, setMs] = useState(false);
  const [autoMsg, setAutoMsg] = useState("");
  const branding = useBranding();
  const suiteName = branding?.suite_name || BRAND;

  const launch = async (acct) => {
    setErr(""); setBusy(true);
    try {
      const { data: res } = await client.post("/demo/login", { username: acct.username, password: acct.password });
      login(res.token, res.user);
      nav("/app");
    } catch (e) { setErr(apiErr(e)); setBusy(false); }
  };

  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const redeem = async () => {
    if (!code.trim()) { setErr("Enter your access code."); return; }
    setErr(""); setRedeeming(true);
    try {
      const { data: res } = await client.post("/demo/redeem", { code: code.trim().toUpperCase() });
      login(res.token, res.user);
      nav("/app");
    } catch (e) { setErr(apiErr(e)); setRedeeming(false); }
  };

  useEffect(() => {
    const wantLang = (params.get("lang") || "").trim().toUpperCase();
    if (wantLang && LANGS.some((l) => l.code === wantLang)) setLang(wantLang);
    client.get("/demo/accounts").then((r) => {
      setData(r.data);
      const want = (params.get("launch") || "").trim().toLowerCase();
      if (!want) return;
      const all = [...r.data.suite.accounts, ...r.data.single_apps.flatMap((a) => a.accounts)];
      const match = all.find((a) => a.username === want);
      if (match) { setAutoMsg(`Opening ${match.name}…`); launch(match); }
      else setErr(`Demo account "${want}" was not found.`);
    }).catch((e) => setErr(apiErr(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[var(--app-bg,#F4F4F4)]" data-testid="demo-login-page">
      <header className="border-b border-black/10 dark:border-white/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-5 py-4">
          <img src="/brand-logo.png" alt={suiteName} className="h-9 w-9 object-contain dark:brightness-0 dark:invert" />
          <div>
            <div className="font-head text-lg leading-tight">{suiteName} — {tp("Demo Centre", lang)}</div>
            <div className="text-xs opacity-60">{tp("Sandboxed sales & publicity demonstrations · no real data", lang)}</div>
          </div>
          <div className="ms-auto flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-md border border-black/15 px-2 py-1 dark:border-white/15" title={tp("Preview the suite in your language", lang)}>
              <Languages size={14} className="text-[var(--primary)]" />
              <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="demo-language-select"
                className="bg-transparent text-xs outline-none">
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
              </select>
            </div>
            <Button variant="ghost" onClick={() => setMs(true)} data-testid="demo-multiscreen-open">
              <Monitor size={15} className="me-1.5" /> {tp("Split / multi-screen", lang)}
            </Button>
            <a href="/login" className="text-sm text-[var(--primary)] hover:underline" data-testid="demo-back-login">{tp("← Sign in", lang)}</a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-6 flex items-start gap-2 rounded-lg border border-[#F1C21B]/50 bg-[#F1C21B]/15 px-4 py-3 text-sm" data-testid="demo-warning">
          <FlaskConical size={18} className="mt-0.5 shrink-0 text-[#8a6d00] dark:text-[#F1C21B]" />
          <p><b>{tp("DEMO · SIMULATION.", lang)}</b> {tp("These accounts use fully sandboxed, non-live sample data for demonstrations only. Every account is isolated and can run simultaneously. No MFA is required. Pick an account below and click", lang)} <b>{tp("Launch", lang)}</b>.</p>
        </div>
        {err && <p className="mb-4 text-sm text-[#DA1E28]" data-testid="demo-error">{err}</p>}
        {autoMsg && <p className="mb-4 text-sm text-[var(--primary)]" data-testid="demo-auto-launch">{autoMsg}</p>}
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/5 px-4 py-2.5 text-sm" data-testid="demo-lang-hint">
          <Languages size={16} className="shrink-0 text-[var(--primary)]" />
          <span className="opacity-80">{tp("See the suite in your language — switch here, then launch any demo below.", lang)}</span>
        </div>
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/5 px-4 py-3 text-sm" data-testid="demo-redeem-box">
          <div className="flex items-center gap-2">
            <Play size={16} className="shrink-0 text-[var(--primary)]" />
            <span className="font-medium">{tp("Have an access code?", lang)}</span>
          </div>
          <span className="opacity-70">{tp("Enter a one-time code from our team to open a private demo.", lang)}</span>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. UVTZ5JK2" maxLength={12} data-testid="demo-redeem-input"
            className="ms-auto w-40 rounded-md border border-black/15 bg-white/70 px-3 py-1.5 font-mono text-sm uppercase tracking-wider outline-none dark:border-white/15 dark:bg-black/30" />
          <Button onClick={redeem} disabled={redeeming} data-testid="demo-redeem-btn">
            {redeeming ? tp("Opening…", lang) : tp("Open demo", lang)}
          </Button>
        </div>
        {!data && !err && <p className="opacity-60">{tp("Loading demo accounts…", lang)}</p>}

        {data && (
          <>
            <h2 className="mb-1 font-head text-2xl">{tp("Single-application demos", lang)}</h2>
            <p className="mb-4 text-sm opacity-70">{tp("One featured demo per standalone application. Need another private instance? Ask our team for a one-time access code.", lang)}</p>
            <div className="grid gap-5 md:grid-cols-2">
              {data.single_apps.map((app) => {
                const Icon = ICONS[app.app_type] || Boxes;
                return (
                  <section key={app.app_type} className="rounded-xl border border-black/10 bg-white/50 p-4 dark:border-white/10 dark:bg-white/5"
                    data-testid={`demo-app-${app.app_type}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]"><Icon size={18} /></span>
                      <div>
                        <div className="font-head text-lg leading-tight">{app.label}</div>
                        <div className="text-xs opacity-60">{tp("Featured demo", lang)}</div>
                      </div>
                    </div>
                    <p className="mb-3 text-xs opacity-70">{app.description}</p>
                    <div className="space-y-2">
                      {app.accounts.map((a) => <AccountRow key={a.username} acct={a} onLaunch={launch} busy={busy} lang={lang} />)}
                    </div>
                  </section>
                );
              })}
            </div>

            <h2 className="mb-1 mt-10 flex items-center gap-2 font-head text-2xl"><Layers size={22} /> {tp("Full-suite demos", lang)}</h2>
            <p className="mb-4 text-sm opacity-70">{data.suite.label} {tp("— all four apps licensed, activated and interweaving with live cross-app history.", lang)}</p>
            <div className="grid gap-2 md:grid-cols-2">
              {data.suite.accounts.map((a) => <AccountRow key={a.username} acct={a} onLaunch={launch} busy={busy} lang={lang} />)}
            </div>
          </>
        )}
      </main>
      <MultiScreenModal open={ms} onClose={() => setMs(false)} lang={lang} />
      <BrandFooter />
    </div>
  );
}
